import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {WidthItem} from 'src/components/width-item';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Message} from '@pb/kit-component-message';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {pluralize} from '@pb/kit-formatters';

import type {BulkActionResult, GetProgress, GetResult} from './typedef';
import type {ReactElement, ReactNode} from 'react';
import type {BulkProgress} from 'src/openapi/admin/models/BulkProgress';
import type {Error} from 'src/openapi/admin/models/Error';
import type {Pagination} from 'src/openapi/admin/models/Pagination';


type Props<ResultRecord, AdditionalData> = {
    id: string,
    onClose: () => void,

    toolTitle: string,
    table: (result: ResultRecord[], pagination: Pagination) => ReactElement,
    description: (result: BulkActionResult<ResultRecord, AdditionalData>) => ReactNode,

    getProgress: GetProgress,
    getResult: GetResult<ResultRecord, AdditionalData>,

    sortComparator: (a: ResultRecord, b: ResultRecord) => number,

    width?: number,
};

export const BulkActionResultModal = <ResultRecord, AdditionalData = void>({
    id,
    onClose,
    toolTitle,
    description,
    table,
    getProgress,
    getResult,
    sortComparator,
    width,
}: Props<ResultRecord, AdditionalData>) => {
    const [progress, setProgress] = useState<BulkProgress | null>(null);
    const [bulkResult, setBulkResult] = useState<BulkActionResult<ResultRecord, AdditionalData> | null>(null);
    const [{currentPage, pageSize}, setPagination] = useState<Pagination>({currentPage: 1, pageSize: 10});
    const [loadingError, setLoadingError] = useState('');

    const onPaginationChange = useCallback((changedPagination: Pagination) => {
        setPagination(changedPagination);
    }, []);

    useEffect(() => {
        if (!id) {
            return;
        }
        const loadResult = async () => {
            let error: Error | null = null;

            const loadedProgress = await getProgress(id);
            if (loadedProgress.payload.success) {
                setProgress(loadedProgress.payload.data);
            } else {
                error = loadedProgress.payload.data;
            }

            const result = await getResult(id);
            if (result.payload.success) {
                if (result.payload.data.itemList?.length) {
                    setBulkResult({
                        startTime: result.payload.data.startTime,
                        itemList: result.payload.data.itemList.sort(sortComparator),
                        additionalData: result.payload.data.additionalData,
                    });
                } else {
                    error = {message: 'No processed items'};
                }
            } else {
                error = result.payload.data;
            }

            if (error) {
                setLoadingError(getError({error, separator: ' '}));
            }
        };

        loadResult();
    }, [id, getProgress, getResult, sortComparator]);

    const failedResults = useMemo(() => (
        bulkResult?.itemList?.reduce((count, record) => {
            if (record && typeof record === 'object' && 'errors' in record) {
                const errors = (record as Record<string, unknown>).errors;
                if (Array.isArray(errors) && errors.length) {
                    return count + 1;
                }
            }

            return count;
        }, 0) || 0
    ), [bulkResult]);

    if (loadingError) {
        return (
            <Message color="error" onClose={onClose}>
                {loadingError}
            </Message>
        );
    }

    return (
        <>
            <DialogHeader onClose={onClose}>
                {`${toolTitle} Result`}
            </DialogHeader>
            <DialogMain style={{width: width ?? 913}}>
                <Message color={failedResults ? 'warning' : 'success'}>
                    {pluralize(`Completed successfully ${progress?.completed} item[s]. `, progress?.completed)}
                    {!!progress?.failed && pluralize(`${progress?.failed} item[s] failed. `, progress?.failed)}
                    {!!progress?.canceled && pluralize(`${progress?.canceled} item[s] canceled.`, progress?.canceled)}
                </Message>
                <Group direction="v" style={{padding: 24}}>
                    <WidthItem width="100%">
                        {bulkResult ? description(bulkResult) : null}
                    </WidthItem>
                    <WidthItem width="100%">
                        {bulkResult?.itemList ? table(bulkResult.itemList, {currentPage, pageSize}) : null}
                    </WidthItem>
                    <PaginationStripe
                        pageSize={pageSize}
                        totalCount={bulkResult?.itemList?.length || 0}
                        currentPage={currentPage}
                        onPaginationChange={onPaginationChange}
                        goToPage={true}
                        sizeSwitcherPosition="LEFT"
                        sizeSwitcherType="ONLY_PAGE_SIZE"
                        sizes={[5, 10]}
                    />
                </Group>
            </DialogMain>
            <DialogFooter>
                <Button variant="Type.PRIMARY" onClick={onClose}>
                    Close
                </Button>
            </DialogFooter>
        </>
    );
};
